import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, CalendarCheck, DollarSign, Users, Wrench,
  TrendingUp, TrendingDown, Bike, AlertCircle, ArrowRight,
  MessageSquare, CheckCircle
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { reportAPI, bookingAPI, maintenanceAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const StatCard = ({ label, value, icon: Icon, color, bg, change, prefix = '' }) => (
  <div className="stat-card">
    <div className="stat-icon" style={{ background: bg }}>
      <Icon size={22} color={color} />
    </div>
    <div>
      <div className="stat-value">{prefix}{typeof value === 'number' ? value.toLocaleString() : value}</div>
      <div className="stat-label">{label}</div>
      {change !== undefined && (
        <div className={`stat-change ${change >= 0 ? 'up' : 'down'}`}>
          {change >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          {' '}{Math.abs(change)}% vs last month
        </div>
      )}
    </div>
  </div>
);

const DashboardPage = () => {
  const { user, canManage } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [maintenanceTickets, setMaintenanceTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [statsRes, revenueRes, bookingsRes, maintenanceRes] = await Promise.all([
          reportAPI.getDashboard(),
          reportAPI.getRevenue({ groupBy: 'month' }),
          bookingAPI.getAll({ limit: 5, page: 1 }),
          maintenanceAPI.getAll({ status: 'open', limit: 5 })
        ]);
        setStats(statsRes.data.data);
        setRevenueData(revenueRes.data.data.propertyRevenue || []);
        setRecentBookings(bookingsRes.data.data || []);
        setMaintenanceTickets(maintenanceRes.data.data || []);
      } catch (err) {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) return (
    <div className="loading-center"><div className="spinner spinner-lg" /></div>
  );

  const statCards = [
    { label: 'Active Properties', value: stats?.totalProperties || 0, icon: Building2, color: '#3B82F6', bg: '#EFF6FF' },
    { label: 'Active Bookings', value: stats?.activeBookings || 0, icon: CalendarCheck, color: '#10B981', bg: '#ECFDF5' },
    { label: 'Monthly Revenue', value: stats?.monthlyRevenue || 0, icon: DollarSign, color: '#F59E0B', bg: '#FFFBEB', change: stats?.revenueGrowth, prefix: '$' },
    { label: 'Total Guests', value: stats?.totalGuests || 0, icon: Users, color: '#8B5CF6', bg: '#F5F3FF' },
    { label: 'Pending Maintenance', value: stats?.pendingMaintenance || 0, icon: Wrench, color: '#EF4444', bg: '#FEF2F2' },
    { label: 'New CRM Leads', value: stats?.newLeads || 0, icon: MessageSquare, color: '#06B6D4', bg: '#ECFEFF' },
    { label: 'Active Equipment', value: stats?.equipmentActive || 0, icon: Bike, color: '#84CC16', bg: '#F7FEE7' },
    { label: 'Check-ins (7 days)', value: stats?.upcomingCheckIns || 0, icon: CheckCircle, color: '#10B981', bg: '#ECFDF5' },
  ];

  const statusColors = {
    confirmed: '#10B981', pending: '#F59E0B', checked_in: '#3B82F6',
    checked_out: '#6B7280', cancelled: '#EF4444'
  };

  const statusBg = {
    confirmed: '#ECFDF5', pending: '#FFFBEB', checked_in: '#EFF6FF',
    checked_out: '#F9FAFB', cancelled: '#FEF2F2'
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 18 ? 'Afternoon' : 'Evening'}, {user?.firstName}!</h1>
          <p className="page-subtitle">Here's what's happening with your properties today.</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/bookings')}>
          <CalendarCheck size={16} /> View All Bookings
        </button>
      </div>

      {/* Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, marginBottom: 28 }}>
        {statCards.map(card => <StatCard key={card.label} {...card} />)}
      </div>

      {/* Overdue Alert */}
      {stats?.overdueEquipment > 0 && (
        <div style={{
          background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10,
          padding: '12px 16px', marginBottom: 24, display: 'flex', alignItems: 'center',
          gap: 10, color: '#DC2626'
        }}>
          <AlertCircle size={18} />
          <span style={{ fontWeight: 500 }}>{stats.overdueEquipment} equipment item(s) overdue for return.</span>
          <button className="btn btn-sm" onClick={() => navigate('/equipment/operations')}
            style={{ marginLeft: 'auto', background: '#DC2626', color: '#fff' }}>
            View Now <ArrowRight size={14} />
          </button>
        </div>
      )}

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Revenue Chart */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Revenue Overview</span>
            <button className="btn btn-sm btn-secondary" onClick={() => navigate('/reports')}>
              Full Report <ArrowRight size={14} />
            </button>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="_id" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, 'Revenue']} />
              <Line type="monotone" dataKey="revenue" stroke="#3B82F6" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Quick Stats */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Upcoming</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { label: 'Check-ins (7d)', value: stats?.upcomingCheckIns || 0, color: '#10B981', bg: '#ECFDF5' },
              { label: 'Check-outs (2d)', value: stats?.upcomingCheckOuts || 0, color: '#3B82F6', bg: '#EFF6FF' },
              { label: 'Overdue Equipment', value: stats?.overdueEquipment || 0, color: '#EF4444', bg: '#FEF2F2' },
            ].map(({ label, value, color, bg }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: bg, borderRadius: 8 }}>
                <span style={{ fontSize: 14, color: '#374151' }}>{label}</span>
                <span style={{ fontSize: 20, fontWeight: 700, color }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Bookings & Maintenance */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Recent Bookings */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Recent Bookings</span>
            <button className="btn btn-sm btn-ghost" onClick={() => navigate('/bookings')}>
              View all <ArrowRight size={14} />
            </button>
          </div>
          {recentBookings.length === 0 ? (
            <div className="empty-state" style={{ padding: '24px 0' }}>
              <CalendarCheck size={32} />
              <p>No bookings yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {recentBookings.map(b => (
                <div key={b._id} onClick={() => navigate(`/bookings/${b._id}`)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #F3F4F6', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#111827' }}>
                      {b.guest?.firstName} {b.guest?.lastName}
                    </div>
                    <div style={{ fontSize: 12, color: '#6B7280' }}>
                      {b.property?.title} · {b.nights} nights
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span className="badge" style={{ background: statusBg[b.status], color: statusColors[b.status] }}>
                      {b.status?.replace('_', ' ')}
                    </span>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginTop: 4 }}>
                      ${b.pricing?.total?.toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Maintenance Tickets */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Open Maintenance</span>
            <button className="btn btn-sm btn-ghost" onClick={() => navigate('/maintenance')}>
              View all <ArrowRight size={14} />
            </button>
          </div>
          {maintenanceTickets.length === 0 ? (
            <div className="empty-state" style={{ padding: '24px 0' }}>
              <Wrench size={32} />
              <p>No open tickets</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {maintenanceTickets.map(t => {
                const priorityColors = { urgent: '#DC2626', high: '#F97316', medium: '#F59E0B', low: '#6B7280' };
                return (
                  <div key={t._id} onClick={() => navigate(`/maintenance`)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #F3F4F6', cursor: 'pointer' }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: '#111827' }}>{t.title}</div>
                      <div style={{ fontSize: 12, color: '#6B7280' }}>
                        {t.property?.title || t.equipment?.name} · #{t.ticketNumber}
                      </div>
                    </div>
                    <span className="badge" style={{ color: priorityColors[t.priority], background: `${priorityColors[t.priority]}15` }}>
                      {t.priority}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
